import {
  closestCorners,
  getFirstCollision,
  KeyboardCode,
  type DroppableContainer,
  type KeyboardCoordinateGetter,
} from '@dnd-kit/core';
import type {
  SensorContext,
  SortableTreeBaseContainer,
  SortableTreeBaseItem,
} from './types';
import { getProjection } from './utils';

// String aliases of the (string) enum members, so comparisons against the
// plain `event.code` string stay type-safe (no enum/string mismatch).
const DOWN: string = KeyboardCode.Down;
const UP: string = KeyboardCode.Up;
const LEFT: string = KeyboardCode.Left;
const RIGHT: string = KeyboardCode.Right;

const directions: string[] = [DOWN, UP, LEFT, RIGHT];
const horizontal: string[] = [LEFT, RIGHT];

/**
 * Keyboard navigation for {@link SortableTree}, mirroring pointer dragging:
 * Left/Right jump to the slot's next valid depth (per `getProjection`, so the
 * consumer's `canDrop` applies), Up/Down move tree-aware between rows.
 * Ported from dnd-kit's Tree example; reads live drag state from `context`
 * because the getter runs outside React render.
 */
export const sortableTreeKeyboardCoordinates =
  <
    TItem extends SortableTreeBaseItem,
    TContainer extends SortableTreeBaseContainer = SortableTreeBaseContainer,
  >(
    context: SensorContext<TItem, TContainer>,
    indentationWidth: number,
    maxDepth?: number
  ): KeyboardCoordinateGetter =>
  (
    event,
    {
      currentCoordinates,
      context: {
        active,
        over,
        collisionRect,
        droppableRects,
        droppableContainers,
      },
    }
  ) => {
    if (!directions.includes(event.code) || !active || !collisionRect) {
      return undefined;
    }

    event.preventDefault();

    const { items, offset, canDrop } = context.current;

    if (horizontal.includes(event.code) && over?.id) {
      const activeItem = items.find(({ id }) => id === active.id);
      if (!activeItem) {
        return undefined;
      }

      const { depth, validDepths } = getProjection(
        items,
        active.id,
        over.id,
        offset,
        indentationWidth,
        maxDepth,
        canDrop
      );

      // Jump straight to the next valid depth: the projection snaps an invalid
      // one back, so stepping a single level could look like nothing happened.
      const target =
        event.code === LEFT
          ? validDepths.findLast((candidate) => candidate < depth)
          : validDepths.find((candidate) => candidate > depth);

      if (target == null) {
        return undefined;
      }

      const offsetDepth =
        activeItem.depth + Math.round(offset / indentationWidth);

      return {
        ...currentCoordinates,
        x: currentCoordinates.x + (target - offsetDepth) * indentationWidth,
      };
    }

    const containers: DroppableContainer[] = [];

    droppableContainers.forEach((container) => {
      if (container?.disabled || container.id === over?.id) {
        return;
      }

      const rect = droppableRects.get(container.id);

      if (!rect) {
        return;
      }

      if (event.code === DOWN && collisionRect.top < rect.top) {
        containers.push(container);
      } else if (event.code === UP && collisionRect.top > rect.top) {
        containers.push(container);
      }
    });

    const collisions = closestCorners({
      active,
      collisionRect,
      pointerCoordinates: null,
      droppableRects,
      droppableContainers: containers,
    });
    let closestId = getFirstCollision(collisions, 'id');

    if (closestId === over?.id && collisions.length > 1) {
      closestId = collisions[1].id;
    }

    if (closestId == null || !over?.id) {
      return undefined;
    }

    const activeRect = droppableRects.get(active.id);
    const newRect = droppableRects.get(closestId);
    const newDroppable = droppableContainers.get(closestId);

    if (!activeRect || !newRect || !newDroppable) {
      return undefined;
    }

    const newIndex = items.findIndex(({ id }) => id === closestId);
    const activeIndex = items.findIndex(({ id }) => id === active.id);
    const newItem = items[newIndex];
    const activeItem = items[activeIndex];

    if (!newItem || !activeItem) {
      return undefined;
    }

    const { depth } = getProjection(
      items,
      active.id,
      closestId,
      (newItem.depth - activeItem.depth) * indentationWidth,
      indentationWidth,
      maxDepth
    );

    // The active row is collapsed to the drop indicator while the collision
    // rect keeps the full row height, so centre the rect on the target slot.
    const slotOffset = (collisionRect.height - activeRect.height) / 2;
    const direction = newIndex > activeIndex ? 1 : -1;

    return {
      x: newRect.left + depth * indentationWidth,
      y: newRect.top + direction * slotOffset,
    };
  };
